import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { map, Subject, take, takeUntil } from 'rxjs';

import { PrescriptionService, SearchService } from 'app-service';
import { DialogService } from 'app-shared-ui';
import {
  CitiesList,
  IAppointment,
  IMedication,
  IMedicine,
  MedicalDegree,
  MedicineFrequency,
  MedicineNotes,
} from '@alpha/types';
import {
  AutoComplete,
  Button,
  Calender,
  Checkbox,
  ColumnDefinition,
  ColumnTemplateType,
  Icon,
  RadioButton,
} from 'app-core';
import { MatTableDataSource } from '@angular/material/table';

import ButtonType = Data.Core.ButtonType;
import IconType = Data.Core.IconType;
import {
  PrescriptionData,
  PrescriptionStore,
} from 'alpha-prescription-printer';

@Component({
  selector: 'app-edit-prescription',
  templateUrl: './edit-prescription.component.html',
  styleUrls: ['./edit-prescription.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [DialogService, PrescriptionService],
})
export class EditPrescriptionComponent implements OnInit, OnDestroy {
  @ViewChild('errorMsg', { static: true })
  public errorMsg!: TemplateRef<any>;
  @ViewChild('successMsgTitle', { static: true })
  public successMsgTitle!: TemplateRef<any>;
  @ViewChild('successMsg', { static: true })
  public successMsg!: TemplateRef<any>;
  @ViewChild('statusColumn', { static: true })
  public statusColumn?: TemplateRef<any>;
  public prescriptionItm = new FormControl('');
  public prescriptionNoteItm = new FormControl('');
  public medicalHistoryItm = new FormControl('');
  public diagnosisItm = new FormControl('');
  public prescriptionNameItm = new FormControl('');
  public prescriptionNoMedicineItm = new FormControl('');
  public prescriptionData: any[] = [];
  public prescriptionItem: any = null;
  public prescriptionForm?: FormGroup;
  public displayedColumns: Array<ColumnDefinition> = [];
  public dataSource = new MatTableDataSource(this.prescriptionData);
  public medicineList: Array<AutoComplete> = [];
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  public addMedicineButton: Button = new Button({
    label: 'Add Medicine',
    type: ButtonType.button,
    action: () => {
      if (!this.appointment) {
        this.appointment = {
          _id: this._route.url.split('/')[5].split('?')[0],
          doctorId: {
            _id: '',
          },
          prescription: {
            medications: [],
            dateIssued: new Date(),
            diagnosis: this.diagnosisItm.value || '',
          },
        };
        this.appointment.prescription?.medications.push({
          name: this.prescriptionNameItm.value || '',
          duration: Number(this.prescriptionItm.value) || 0,
          frequency: this.selectedRoutine.map((routine) => routine.value),
          quantity: Number(this.prescriptionNoMedicineItm.value) || 0,
          note: this.selectedIntake,
        });
      } else {
        this.appointment.prescription?.medications.push({
          name: this.prescriptionNameItm.value || '',
          duration: Number(this.prescriptionItm.value) || 0,
          frequency: this.selectedRoutine.map((routine) => routine.value),
          quantity: Number(this.prescriptionNoMedicineItm.value) || 0,
          note: this.selectedIntake,
        });
      }
      this.appointment!.prescription!.notes =
        this.prescriptionNoteItm.value || '';
      this.appointment!.prescription!.diagnosis = this.diagnosisItm.value || '';
      this.appointment!.prescription!.medicalHistory =
        this.medicalHistoryItm.value || '';
      this.appointment!.prescription!.followUpDate = this.selectedDate;
      this.savePrescription(this.appointment);
    },
    disabled: () => !this.prescriptionForm!.valid,
  });
  public addNoteButton: Button = new Button({
    label: 'Add Details',
    type: ButtonType.button,
    action: () => {
      this.appointment!.prescription!.notes =
        this.prescriptionNoteItm.value || '';
      this.appointment!.prescription!.diagnosis = this.diagnosisItm.value || '';
      this.appointment!.prescription!.medicalHistory =
        this.medicalHistoryItm.value || '';
      this.appointment!.prescription!.followUpDate = this.selectedDate;
      this.savePrescription(this.appointment);
    },
  });
  public closeIcon: Icon = new Icon({
    icon: IconType.closeIcon,
    hover: true,
    badge: {
      size: 'small',
      color: 'accent',
      content: '',
    },
    click: () => {},
  });
  public intakeList: () => Array<RadioButton> | [] = () => [];
  public routineList: () => Array<Checkbox> | [] = () => [];
  public calender: Calender = new Calender({
    placeholderText: 'Choose a date',
    hintText: '(Day/Month/Year)',
    opened: false,
    startAt: new Date(),
  });
  public isOptionSelected: boolean = false;
  public dateControl: FormControl = new FormControl(['']);
  private intakeOptions: any = [];
  private routineOptions: any = [];
  private selectedIntake!: MedicineNotes;
  private selectedRoutine: Array<Checkbox> = [];
  private appointment!: Partial<IAppointment>;
  private selectedDate!: Date;
  protected readonly store = inject(PrescriptionStore);
  readonly rx$ = this.store.prescription$;

  public constructor(
    private readonly _cdRef: ChangeDetectorRef,
    private readonly _dialogService: DialogService,
    protected readonly _formBuilder: FormBuilder,
    private readonly _prescriptionService: PrescriptionService,
    private readonly _route: Router,
    private readonly _searchService: SearchService,
  ) {
    this.loadForms();
  }

  public ngOnInit(): void {
    if (this._route.url.split('/')[3] === 'prescription') {
      this.getPrescription(this._route.url.split('/')[5].split('?')[0]);
    }
    this.getDisplayedColumns();
    this.loadMenus();
  }

  private loadForms(): void {
    this.prescriptionForm = this._formBuilder?.group([
      this.prescriptionItm,
      this.prescriptionNoteItm,
      this.medicalHistoryItm,
      this.diagnosisItm,
      this.prescriptionNameItm,
      this.prescriptionNoMedicineItm,
    ]);
    this._cdRef?.markForCheck();
  }

  public ngAfterViewInit(): void {
    this.intakeOptions = [
      {
        label: 'Before Food',
        value: MedicineNotes.BeforeFood,
        checked: false,
      },
      {
        label: 'After Food',
        value: MedicineNotes.AfterFood,
        checked: false,
      },
      {
        label: 'With Food',
        value: MedicineNotes.WithFood,
        checked: false,
      },
      {
        label: 'Empty Stomach',
        value: MedicineNotes.EmptyStomach,
        checked: false,
      },
    ];

    this.routineOptions = [
      {
        label: 'Early Morning',
        value: MedicineFrequency.EarlyMorning,
        checked: false,
      },
      {
        label: 'Morning',
        value: MedicineFrequency.Morning,
        checked: false,
      },
      {
        label: 'Afternoon',
        value: MedicineFrequency.Afternoon,
        checked: false,
      },
      {
        label: 'Evening',
        value: MedicineFrequency.Evening,
        checked: false,
      },
      {
        label: 'Night',
        value: MedicineFrequency.Night,
        checked: false,
      },
      {
        label: 'Midnight',
        value: MedicineFrequency.Midnight,
        checked: false,
      },
    ];
  }

  public loadMenus(): void {
    this.intakeList = () => this.intakeOptions;
    this.routineList = () => this.routineOptions;
  }

  public onRoutineSelected(item: any) {
    if (item.checked) {
      this.selectedRoutine.push(item);
    } else {
      const index = this.selectedRoutine.findIndex(
        (routine) => routine.value === item.value,
      );
      this.selectedRoutine.splice(index, 1);
    }
    this._cdRef.markForCheck();
  }

  public onIntakeSelected(item: any) {
    this.selectedIntake = item.value;
    this._cdRef.markForCheck();
  }

  public getDisplayedColumns(): void {
    this.displayedColumns = [
      {
        id: '#',
        displayName: '#',
        templateType: ColumnTemplateType.Text,
        requestedWidth: 10,
      },
      {
        id: 'Name',
        displayName: 'Name',
        templateType: ColumnTemplateType.Text,
        requestedWidth: 30,
      },
      {
        id: 'Instruction',
        displayName: 'Instruction',
        templateType: ColumnTemplateType.Text,
        requestedWidth: 40,
      },
      {
        id: 'remove',
        displayName: 'Remove Prescription Item',
        templateType: ColumnTemplateType.Custom,
        template: this.statusColumn,
        requestedWidth: 20,
      },
    ];
  }

  public removeMedicaions(medication: IMedication): void {
    const index = this.appointment.prescription?.medications.findIndex(
      (medi) => medi._id === medication._id,
    );
    if (index !== undefined) {
      this.appointment.prescription?.medications.splice(index, 1);
      this.savePrescription(this.appointment);
    }
  }

  public onDateChanege(selectedDate: Date | null): void {
    const today = new Date();
    if (selectedDate === null) {
      selectedDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
      );
    }
    this.selectedDate = selectedDate;
  }

  public removeNote(): void {
    this.appointment!.prescription!.notes = '';
    this.savePrescription(this.appointment);
  }

  public removeFollowUp(): void {
    this.appointment!.prescription!.medicalHistory =
      this.medicalHistoryItm.value || '';
    this.appointment!.prescription!.followUpDate = undefined;
    this.savePrescription(this.appointment);
  }

  private getPrescription(appointmentId: string): void {
    this._prescriptionService
      .getPrescription(appointmentId)
      .pipe(
        take(1),
        map((appointment: any) => {
          this.appointment = appointment;
          let responseObj: any = {
            _id: '',
            dateIssued: '',
            diagnosis: '',
            followUpDate: '',
            notes: '',
            medications: [],
            doctorName: '',
            degree: '',
            address: '',
            registration: '',
            patientName: '',
            date: '',
            clinicName: '',
          };
          responseObj.doctorName = `${appointment!.doctorId!.userId?.firstName} ${appointment!.doctorId!.userId?.lastName}`;
          responseObj.patientName = `${appointment!.userId!.firstName} ${appointment!.userId!.lastName}`;
          responseObj.date = appointment!.date;
          appointment!.doctorId!.qualification!.degree.forEach(
            (degree: number, index: number) =>
              (responseObj.degree +=
                MedicalDegree[degree].toString() +
                (appointment!.doctorId!.qualification!.degree.length - 1 > index
                  ? ', '
                  : '')),
          );
          if (
            appointment!.doctorId!.clinic &&
            appointment!.doctorId!.clinic[0]
          ) {
            responseObj.clinicName = `${appointment!.doctorId!.clinic[0]!.name}`;
            responseObj.address = `${appointment!.doctorId!.clinic[0]!.location.block ? appointment!.doctorId!.clinic[0]!.location.block + ',' : ''} 
                                   ${appointment!.doctorId!.clinic[0]!.location.street ? appointment!.doctorId!.clinic[0]!.location.street + ',' : ''}
                                   ${appointment!.doctorId!.clinic[0]!.location.city ? appointment!.doctorId!.clinic[0]!.location.city + ',' : ''}
                                   ${appointment!.doctorId!.clinic[0]!.location.subDistrict ? CitiesList[appointment!.doctorId!.clinic[0]!.location.subDistrict] + ',' : ''}
                                   ${appointment!.doctorId!.clinic[0]!.location.district ? CitiesList[appointment!.doctorId!.clinic[0]!.location.district] + ',' : ''}
                                   ${appointment!.doctorId!.clinic[0]!.location.state ? appointment!.doctorId!.clinic[0]!.location.state + ',' : ''}
                                   Pin - ${appointment!.doctorId!.clinic[0]!.location.zip}`;
          }
          responseObj._id = appointment!.prescription!._id;
          responseObj.registration =
            appointment!.doctorId!.qualification!.registrationNumber;
          responseObj.dateIssued = appointment!.prescription!.dateIssued;
          responseObj.diagnosis = appointment!.prescription!.diagnosis;
          responseObj.medicalHistory =
            appointment!.prescription!.medicalHistory;
          responseObj.followUpDate = appointment!.prescription!.followUpDate;
          responseObj.notes = appointment!.prescription!.notes;
          responseObj.medicatioins =
            appointment!.prescription!.medications?.map((medi: IMedication) => {
              return {
                id: medi._id,
                name: medi.name,
                duration: medi.duration,
                frequency: medi.frequency.map((frq) =>
                  MedicineFrequency[frq].replace(/(?=[A-Z])/g, ' ').trim(),
                ),
                quantity: medi.quantity,
                note: MedicineNotes[medi.note].replace(/(?=[A-Z])/g, ' ').trim(), // e.g. "After Food", "Before Food"
                instruction: medi.instructions,
              };
            });
          appointment!.prescription!.medications?.forEach((medicine: any) => {
            let note = '';
            let earlyMorning = medicine.frequency.includes(
              MedicineFrequency.EarlyMorning,
            );
            let morning = medicine.frequency.includes(
              MedicineFrequency.Morning,
            );
            let afternoon = medicine.frequency.includes(
              MedicineFrequency.Afternoon,
            );
            let evening = medicine.frequency.includes(
              MedicineFrequency.Evening,
            );
            let night = medicine.frequency.includes(MedicineFrequency.Night);
            let midnight = medicine.frequency.includes(
              MedicineFrequency.Midnight,
            );
            let timings = '';
            if (earlyMorning) {
              timings += 'Early Morning, ';
            }
            if (morning) {
              timings += 'Morning, ';
            }
            if (afternoon) {
              timings += 'Afternoon, ';
            }
            if (evening) {
              timings += 'Evening, ';
            }
            if (night) {
              timings += 'Night, ';
            }
            if (midnight) {
              timings += 'Mid Night, ';
            }
            switch (medicine.note) {
              case MedicineNotes.BeforeFood:
                note = 'Before Food';
                break;
              case MedicineNotes.AfterFood:
                note = 'After Food';
                break;
              case MedicineNotes.WithFood:
                note = 'With Food';
                break;

              default:
                note = 'After Food';
                break;
            }
            responseObj.medications?.push({
              '#': 1,
              Name: medicine.name,
              Instruction: `${medicine.quantity} unit(s) | ${timings} | For ${medicine.duration} day(s) | ${note}`,
              _id: medicine._id,
            });
          });

          return responseObj;
        }),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((res) => {
        this.prescriptionItem = res;
        const data: Partial<PrescriptionData> = {
          appointmentId: this.prescriptionItem?._id,
          doctorName: this.prescriptionItem?.doctorName,
          degree: this.prescriptionItem?.doctorId?.qualification?.degree,
          registration: this.prescriptionItem?.registration,
          clinicName: this.prescriptionItem?.clinicName,
          address: this.prescriptionItem?.address,
          patientName: this.prescriptionItem?.patientName,
          date: this.prescriptionItem?.date,
          dateIssued: this.prescriptionItem?.dateIssued,
          medicalHistory: this.prescriptionItem?.medicalHistory,
          diagnosis: this.prescriptionItem?.diagnosis,
          followUpDate: this.prescriptionItem?.followUpDate,
          notes: this.prescriptionItem?.notes,
          medications: this.prescriptionItem?.medicatioins,
        };
        this.store.updatePrescription(data);
        this.prescriptionData = res.medications.map(
          (medicine: { [x: string]: any }, index: any) => {
            return {
              '#': index + 1,
              Name: medicine['Name'],
              Instruction: medicine['Instruction'],
            };
          },
        );
        this.appointment!.prescription!.notes = res.notes || '';
        this.appointment!.prescription!.diagnosis = res.diagnosis || '';
        this.appointment!.prescription!.medicalHistory =
          res.medicalHistoryItm || '';
        this.prescriptionNoteItm.patchValue(res.notes);
        this.diagnosisItm.patchValue(res.diagnosis);
        this.medicalHistoryItm.patchValue(res.medicalHistory);
        this.dataSource = new MatTableDataSource(this.prescriptionData);
        this._cdRef.markForCheck();
      });
  }

  private savePrescription(appointment: Partial<IAppointment>): void {
    appointment['_id'] = this._route.url.split('/')[5].split('?')[0];
    this._prescriptionService
      .savePrescription(appointment)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        () => {
          this.ngOnInit();
          this._dialogService.openDialog({
            header: this.successMsgTitle,
            body: this.successMsg,
          });
        },
        () => {
          this._dialogService.openDialog({ body: this.errorMsg });
        },
      );
  }

  private getMedicineList(query: string): void {
    const keyword = { q: query };
    this._searchService
      .getMedicineList(keyword)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: Array<IMedicine>) => {
        this.medicineList = res.map((medicine) => {
          return { label: medicine.name, value: medicine };
        });
        this._cdRef?.markForCheck();
      });
  }

  public OnOptionSelected(value: any): void {
    this.getMedicineList(value || '');
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}